import * as React from 'react';
import { useAuth } from '../auth';
import {
  ClaimsPanel,
  PhoneVerificationPanel,
  ProviderAccessLoading,
  ProviderAccessUnavailable,
  ProviderAccessWelcome,
  ProviderAccountHeader,
  ProviderSearchPanel,
  VerificationSteps,
} from './provider-access-ui.jsx';

export const ProviderAccessScreen = ({ go }) => {
  const auth = useAuth();
  const [profile, setProfile] = React.useState(null);
  const [claims, setClaims] = React.useState([]);
  const [loadingAccount, setLoadingAccount] = React.useState(false);
  const [error, setError] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [challenge, setChallenge] = React.useState(null);
  const [code, setCode] = React.useState('');
  const [providerSearch, setProviderSearch] = React.useState('');
  const [providers, setProviders] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [selectedProvider, setSelectedProvider] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const loadAccount = React.useCallback(async () => {
    if (!auth.account) return;
    setLoadingAccount(true);
    setError('');
    try {
      const [accountResponse, claimsResponse] = await Promise.all([
        auth.apiFetch('/api/account/me'),
        auth.apiFetch('/api/provider-claims'),
      ]);
      setProfile(accountResponse.data);
      setClaims(claimsResponse.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingAccount(false);
    }
  }, [auth.account, auth.apiFetch]);

  React.useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const startVerification = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const response = await auth.apiFetch(
        '/api/account/phone-verification/start',
        {
          method: 'POST',
          body: JSON.stringify({ phone }),
        },
      );
      setChallenge(response.data);
      setCode('');
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const confirmVerification = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const response = await auth.apiFetch(
        '/api/account/phone-verification/confirm',
        {
          method: 'POST',
          body: JSON.stringify({
            challengeId: challenge.challengeId,
            code,
          }),
        },
      );
      setProfile(response.data);
      setChallenge(null);
      setCode('');
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const searchProviders = async (event) => {
    event.preventDefault();
    if (providerSearch.trim().length < 2) return;
    setSearching(true);
    setError('');
    try {
      const params = new URLSearchParams({
        search: providerSearch.trim(),
        limit: '10',
      });
      const response = await fetch(`/api/providers?${params}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error?.message || 'Search failed');
      setProviders(body.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSearching(false);
    }
  };

  const submitClaim = async () => {
    if (!selectedProvider) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await auth.apiFetch('/api/provider-claims', {
        method: 'POST',
        body: JSON.stringify({ providerId: selectedProvider.id }),
      });
      setClaims((current) => [response.data, ...current]);
      setSelectedProvider(null);
      setProviders([]);
      setProviderSearch('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (auth.loading) {
    return <ProviderAccessLoading>Preparing secure provider sign-in…</ProviderAccessLoading>;
  }

  if (!auth.configured) {
    return (
      <ProviderAccessUnavailable
        message={auth.error?.message || 'Identity configuration has not loaded.'}
        onReturn={() => go('home')}
      />
    );
  }

  if (!auth.account) {
    return (
      <ProviderAccessWelcome
        onSignIn={auth.signIn}
        onPreview={() => go('onboarding')}
      />
    );
  }

  return (
    <div className="provider-access-page route-in">
      <ProviderAccountHeader
        isAdmin={profile?.isAdmin}
        onReview={() => go('claim-review')}
        onSignOut={auth.signOut}
      />

      <section className="container provider-access-content">
        {error && <div className="provider-access-error" role="alert">{error}</div>}
        {loadingAccount || !profile ? (
          <ProviderAccessLoading>Loading your account…</ProviderAccessLoading>
        ) : (
          <>
            <VerificationSteps profile={profile}/>

            {!profile.phoneVerifiedAt && (
              <PhoneVerificationPanel
                phone={phone}
                onPhoneChange={setPhone}
                challenge={challenge}
                code={code}
                onCodeChange={setCode}
                onStart={startVerification}
                onConfirm={confirmVerification}
              />
            )}

            {profile.phoneVerifiedAt && (
              <ProviderSearchPanel
                search={providerSearch}
                onSearchChange={setProviderSearch}
                onSearch={searchProviders}
                searching={searching}
                providers={providers}
                selectedProvider={selectedProvider}
                onSelect={setSelectedProvider}
                onSubmit={submitClaim}
                submitting={submitting}
              />
            )}
            <ClaimsPanel
              claims={claims}
              onManage={(claimedProviderId) =>
                go('provider-workspace', { providerId: claimedProviderId })
              }
            />
          </>
        )}
      </section>
    </div>
  );
};
